import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Handshake, ArrowRight, CheckCircle2, Globe, Shield, Users,
  Truck, Ship, Plane, Warehouse, FileCheck, Phone, Mail,
  Building2, Award, TrendingUp, Clock, MapPin, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const PartnersPage = () => {
  const { t } = useTranslation();

  // Partner categories
  const partnerCategories = [
    {
      icon: Truck,
      title: t('partnersPage.categories.roadCarriers.title'),
      description: t('partnersPage.categories.roadCarriers.description'),
      criteria: t('partnersPage.categories.roadCarriers.criteria', { returnObjects: true })
    },
    {
      icon: Ship,
      title: t('partnersPage.categories.oceanCarriers.title'),
      description: t('partnersPage.categories.oceanCarriers.description'),
      criteria: t('partnersPage.categories.oceanCarriers.criteria', { returnObjects: true })
    },
    {
      icon: Plane,
      title: t('partnersPage.categories.airCargo.title'),
      description: t('partnersPage.categories.airCargo.description'),
      criteria: t('partnersPage.categories.airCargo.criteria', { returnObjects: true })
    },
    {
      icon: Warehouse,
      title: t('partnersPage.categories.warehouse.title'),
      description: t('partnersPage.categories.warehouse.description'),
      criteria: t('partnersPage.categories.warehouse.criteria', { returnObjects: true })
    },
    {
      icon: FileCheck,
      title: t('partnersPage.categories.customs.title'),
      description: t('partnersPage.categories.customs.description'),
      criteria: t('partnersPage.categories.customs.criteria', { returnObjects: true })
    },
    {
      icon: Shield,
      title: t('partnersPage.categories.insurance.title'),
      description: t('partnersPage.categories.insurance.description'),
      criteria: t('partnersPage.categories.insurance.criteria', { returnObjects: true })
    }
  ];

  // Why partner with us
  const partnerBenefits = [
    {
      icon: TrendingUp,
      title: t('partnersPage.partnerBenefits.growingBusiness.title'),
      description: t('partnersPage.partnerBenefits.growingBusiness.description')
    },
    {
      icon: Users,
      title: t('partnersPage.partnerBenefits.directCommunication.title'),
      description: t('partnersPage.partnerBenefits.directCommunication.description')
    },
    {
      icon: Clock,
      title: t('partnersPage.partnerBenefits.reliablePayment.title'),
      description: t('partnersPage.partnerBenefits.reliablePayment.description')
    },
    {
      icon: Award,
      title: t('partnersPage.partnerBenefits.mutualReputation.title'),
      description: t('partnersPage.partnerBenefits.mutualReputation.description')
    }
  ];

  // What we expect
  const expectations = t('partnersPage.expectations', { returnObjects: true });

  // What partners can expect from us
  const ourCommitments = t('partnersPage.ourCommitments', { returnObjects: true });

  return (
    <div className="min-h-screen bg-offwhite" data-testid="partners-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-navy-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 50 Q 25 25, 50 50 T 100 50' stroke='%23C7A24E' stroke-width='0.5' fill='none'/%3E%3C/svg%3E")`,
            backgroundSize: '200px 100px'
          }} />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="w-16 h-16 bg-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Handshake className="w-8 h-8 text-gold" />
            </div>
            <span className="inline-block text-gold font-space text-sm tracking-widest uppercase pb-2 border-b-2 border-gold/60 mb-6">
              {t('partnersPage.badge')}
            </span>
            <h1 className="font-space text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('partnersPage.title')}
            </h1>
            <p className="text-platinum-light text-lg md:text-xl leading-relaxed">
              {t('partnersPage.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Partner Categories */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('partnersPage.partnerCategories')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              {t('partnersPage.typesOfPartners')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t('partnersPage.typesSubtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {partnerCategories.map((category, index) => (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-white border border-platinum-light hover:border-gold/30 hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-navy-900/5 flex items-center justify-center mb-4">
                      <category.icon className="w-6 h-6 text-gold" />
                    </div>
                    <h3 className="font-space text-lg font-bold text-navy-900 mb-2">{category.title}</h3>
                    <p className="text-slate-600 text-sm mb-4">{category.description}</p>
                    <div className="pt-4 border-t border-platinum-light">
                      <p className="text-xs font-semibold text-navy-900 mb-2">{t('partnersPage.whatWeLookFor')}</p>
                      <ul className="space-y-1">
                        {Array.isArray(category.criteria) && category.criteria.map((criterion) => (
                          <li key={`${category.title}-${criterion.slice(0, 20)}`} className="flex items-start gap-2 text-xs text-slate-600">
                            <CheckCircle2 className="w-3 h-3 text-gold flex-shrink-0 mt-0.5" />
                            {criterion}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Partner With Us */}
      <section className="py-20 md:py-28 bg-navy-900">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('partnersPage.benefits')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl font-bold text-white mb-4">
              {t('partnersPage.whyPartner')}
            </h2>
            <p className="text-platinum-light max-w-2xl mx-auto">
              {t('partnersPage.whyPartnerSubtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {partnerBenefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-navy-800/50 border border-royal/20 hover:border-gold/30 transition-colors">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-4">
                      <benefit.icon className="w-6 h-6 text-gold" />
                    </div>
                    <h3 className="font-space text-lg font-bold text-white mb-2">{benefit.title}</h3>
                    <p className="text-platinum-light text-sm">{benefit.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mutual Expectations */}
      <section className="py-20 md:py-28 bg-offwhite">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('partnersPage.workingTogether')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              {t('partnersPage.clearExpectations')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t('partnersPage.clearExpectationsSubtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* What We Expect */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-2 border-navy-900/10 bg-white">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-navy-900 flex items-center justify-center">
                      <Users className="w-5 h-5 text-gold" />
                    </div>
                    <h3 className="font-space text-xl font-bold text-navy-900">{t('partnersPage.whatWeExpect')}</h3>
                  </div>
                  <ul className="space-y-4">
                    {Array.isArray(expectations) && expectations.map((item) => (
                      <li key={`expect-${item.slice(0, 25)}`} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>

            {/* What Partners Can Expect */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-2 border-gold/30 bg-gold/5">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-gold flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-navy-900" />
                    </div>
                    <h3 className="font-space text-xl font-bold text-navy-900">{t('partnersPage.whatPartnersExpect')}</h3>
                  </div>
                  <ul className="space-y-4">
                    {Array.isArray(ourCommitments) && ourCommitments.map((item) => (
                      <li key={`commit-${item.slice(0, 25)}`} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-16 bg-white border-y border-platinum-light">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gold" />
              <span>The Netherlands</span>
            </div>
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gold" />
              <span>KvK 42041124</span>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-gold" />
              <span>EORI NL3785680245</span>
            </div>
            <div className="flex items-center gap-2">
              <FileCheck className="w-4 h-4 text-gold" />
              <span>VAT NL005449408B56</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-navy-900">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="font-space text-3xl md:text-4xl font-bold text-white mb-6">
              {t('partnersPage.interestedTitle')}
            </h2>
            <p className="text-platinum-light text-lg mb-8">
              {t('partnersPage.interestedSubtitle')}
            </p>
            <p className="text-platinum-light mb-10">
              {t('partnersPage.interestedNote')}
            </p>
            
            <Link to="/contact">
              <Button className="bg-gradient-to-r from-gold-light via-gold to-gold-dark text-navy-900 hover:shadow-xl hover:shadow-gold/25 px-10 py-6 text-lg font-semibold rounded-lg transition-all duration-300">
                {t('partnersPage.contactToDiscuss')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>

            <div className="flex items-center justify-center gap-6 mt-10 text-platinum-light">
              <a href="tel:+31625381824" className="flex items-center gap-2 hover:text-gold transition-colors">
                <Phone className="w-5 h-5" />
                <span>+31 62 538 1824</span>
              </a>
              <a href="mailto:info@elementarycargo.com" className="flex items-center gap-2 hover:text-gold transition-colors">
                <Mail className="w-5 h-5" />
                <span>info@elementarycargo.com</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PartnersPage;
